import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCheckout } from '@/hooks/useCheckout';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from '@/components/ui/alert-dialog';
import type { CheckoutPlan } from '@/lib/pricing';

interface Preview {
  kind: 'noop' | 'resume' | 'switch' | 'new_checkout';
  direction: 'upgrade' | 'downgrade' | 'cycle_change' | null;
  currentLabel: string | null;
  currentPriceShort: string | null;
  targetLabel: string;
  targetPriceShort: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

interface Props {
  open: boolean;
  plan: CheckoutPlan | null;
  onOpenChange: (open: boolean) => void;
  onConfirmed?: () => void;
}

function prorationCopy(p: Preview): string {
  if (p.kind === 'resume') {
    return 'This cancels your scheduled cancellation. No charge today.';
  }
  if (p.kind === 'new_checkout') {
    return "You'll be taken to a secure Lemon Squeezy checkout to complete payment.";
  }
  if (p.direction === 'upgrade' || p.direction === 'cycle_change') {
    return "You'll be charged the prorated difference today - not the full new price. Unused time on your current plan is credited toward the new one. Lemon Squeezy will email the exact invoice.";
  }
  if (p.direction === 'downgrade') {
    return 'No charge today. Unused time on your current plan becomes account credit applied to your next renewals. No cash refund is issued.';
  }
  return '';
}

export function PlanChangeConfirmDialog({ open, plan, onOpenChange, onConfirmed }: Props) {
  const { openCheckout, loading: switching } = useCheckout(onConfirmed);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !plan) {
      setPreview(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    supabase.functions
      .invoke('preview-plan-change', { body: { plan } })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setError(error.message || 'Could not load preview');
        } else {
          setPreview(data as Preview);
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, plan]);

  const handleConfirm = async () => {
    if (!plan) return;
    await openCheckout(plan);
    onOpenChange(false);
  };

  const title =
    preview?.kind === 'resume' ? 'Resume your subscription?'
    : preview?.kind === 'noop' ? "You're already on this plan"
    : preview?.kind === 'new_checkout' ? 'Start your subscription'
    : preview?.direction === 'downgrade' ? 'Downgrade your plan?'
    : 'Switch your plan?';

  const confirmLabel =
    preview?.kind === 'resume' ? 'Resume subscription'
    : preview?.kind === 'noop' ? 'OK'
    : preview?.kind === 'new_checkout' ? 'Continue to checkout'
    : 'Confirm switch';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-muted-foreground">
              {loading && <p>Loading preview…</p>}
              {error && <p className="text-destructive">{error}</p>}
              {preview && (
                <>
                  {preview.kind === 'switch' && preview.currentLabel && (
                    <p>
                      Switching from <span className="font-medium text-foreground">{preview.currentLabel}</span>
                      {preview.currentPriceShort ? ` (${preview.currentPriceShort})` : ''} to{' '}
                      <span className="font-medium text-foreground">{preview.targetLabel}</span> ({preview.targetPriceShort}).
                    </p>
                  )}
                  {preview.kind === 'resume' && (
                    <p>
                      You'll continue on <span className="font-medium text-foreground">{preview.targetLabel}</span> ({preview.targetPriceShort}).
                    </p>
                  )}
                  {preview.kind === 'new_checkout' && (
                    <p>
                      <span className="font-medium text-foreground">{preview.targetLabel}</span> - {preview.targetPriceShort}.
                    </p>
                  )}
                  {preview.currentPeriodEnd && preview.kind !== 'new_checkout' && (
                    <p>
                      Current period ends{' '}
                      <span className="text-foreground">{new Date(preview.currentPeriodEnd).toLocaleDateString()}</span>.
                    </p>
                  )}
                  <p className="rounded-lg border border-border/60 bg-muted/30 p-3 text-foreground/80">
                    {prorationCopy(preview)}
                  </p>
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={switching}>
            {preview?.kind === 'noop' ? 'Close' : 'Keep current plan'}
          </Button>
          {preview?.kind !== 'noop' && (
            <Button onClick={handleConfirm} disabled={loading || switching || !preview}>
              {switching ? 'Working…' : confirmLabel}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
